/* This example requires Tailwind CSS v2.0+ */
import { Listbox, Transition } from '@headlessui/react';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cn, isFirstLetterUppercase } from '../../../utils';
import useTranslation from '../../hooks/useTranslation';
import { setSelectedCity } from '../../store/slices/common/citiesSlice';
import {
  getCommunityCategoriesAsync,
  setCommunityCategoriesFromLocalStorage,
} from '../../store/slices/community/communitySlice';
import { Skeleton } from '../Common/Skeleton';
import {
  AiFillCaretDown,
  MdArrowDropDown,
  MdLocationOn,
  MdLocationPin,
  MdOutlineCheck,
} from '../CustomSVG/MaterialIcons';
import { COMMUNITY_ROUTE } from '../../../utils/routes';
import {
  all_cities,
  convertCityNameToSlug,
  getUserCity,
} from '../../../utils/cityUtils';

// IMPORTANT NOTE ON CITIES:
/* 
    1. all_cities are coming from frontend.
    2. It is saved in utils.
    3. Fetching cities from backend was causing issue with city in urls
    4. In future we have to find a solution for this, or
        whenever new city is added or updated in the backend we have to update it
        manually on the frontend
  
  */

const SelectCity = ({ context = 'nav' }) => {
  const dispatch = useDispatch();

  const isFromLandingPage = context === 'landing_page';

  const selectedCity = useSelector(({ cities }) => cities?.selectedCity);

  const router = useRouter();
  const { city, tag_filter = 'most_popular', postType } = router.query;
  const [selected, setSelected] = useState('');

  const handleCityChange = (cityName) => {
    setSelected(cityName); // Update city name in top left city filter
    const selectedCity = saveSelectedCityToLocalStorage(cityName);
    updateCityInUrl(selectedCity[0].slug);

    // To redirect to post type home page if city changes from category page
    if (router?.pathname === '/market/[postType]/[categoryId]') {
      router.replace(
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/${postType}?city=${cityName}&tag_filter=${tag_filter}`
      );
    } else if (
      router?.pathname === '/community/categories/[categoryId]' &&
      parseInt(router?.query?.categoryId) < 13
    ) {
      window.location.href = COMMUNITY_ROUTE(cityName);
    }
  };

  function updateCityInUrl(cityName) {
    router.replace({
      pathname: router.pathname,
      query: { ...router.query, city: cityName },
    });

    setSelected(cityName);
  }

  function saveSelectedCityToLocalStorage(cityName) {
    const selectedCity = all_cities.filter(
      (item) => item.slug === convertCityNameToSlug(cityName)
    );
    dispatch(setSelectedCity(selectedCity[0]));

    return selectedCity;
  }

  function isCityValid(cityName) {
    const selectedCity = all_cities.filter(
      (item) => item.slug === convertCityNameToSlug(cityName)
    );
    if (selectedCity.length > 0) return true;
    return false;
  }

  function getExistingCityId() {
    let existingSelectedCityId = '';
    const existingSelectedCity =
      JSON.parse(localStorage.getItem('categories')) || [];
    if (existingSelectedCity.length > 0) {
      existingSelectedCityId = existingSelectedCity[0]?.city_id;
    }

    return existingSelectedCityId;
  }

  useEffect(() => {
    // This will run whenever city changes and it will fetch appropriate categories
    const existingSelectedCityId = getExistingCityId();
    if (selectedCity?.id && selectedCity?.id !== existingSelectedCityId) {
      dispatch(getCommunityCategoriesAsync({ city_id: selectedCity?.id }));
    } else {
      dispatch(setCommunityCategoriesFromLocalStorage());
    }
  }, [selectedCity?.id]);

  useEffect(() => {
    // This will run on link share or city change on url
    if (city && isCityValid(city)) {
      const selectedCity = saveSelectedCityToLocalStorage(city);
      setSelected(selectedCity[0].name); // Update city name in top left city filter
      // updateCityInUrl(city);
      if (city === 'Zürich' || city === 'zürich') {
        updateCityInUrl('zurich');
      } else if (city === 'Zürich (EN)' || city === 'zürich-en') {
        updateCityInUrl('zurich-en');
      } else if (isFirstLetterUppercase(city)) {
        updateCityInUrl(convertCityNameToSlug(city));
      }
    } else {
      const userCitySlug = getUserCity()?.slug || 'zurich';
      const userCityName = getUserCity()?.name || 'Zürich';
      saveSelectedCityToLocalStorage(userCitySlug);
      setSelected(userCityName); // Update city name in top left city filter
    }
  }, [city]);

  // useEffect(() => {
  //   if (cities === null) dispatch(getCitiesAsync());
  // }, []);

  // Important: The below code was causing dynamic router issue on page reload

  let lang;

  if (!isFromLandingPage) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    lang = useTranslation();
  }

  if (all_cities)
    return (
      <Listbox value={selected} onChange={(event) => handleCityChange(event)}>
        {({ open }) => (
          <>
            {/* <Listbox.Label className='ml-[14px] hidden text-[12px] font-semibold text-primary md:block'>
              {lang?.common?.change_your_city}
            </Listbox.Label> */}
            <div
              className={cn('relative ', isFromLandingPage && 'mt-1 h-[56px]')}
            >
              {isFromLandingPage ? (
                <Listbox.Button className='relative h-[56px] w-[186px] cursor-default rounded-full  border border-gray-300 bg-white  bg-opacity-10 pb-2 pl-5  pr-10 pt-2 text-left text-white shadow-2xl focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm md:h-[56px] md:w-[286px]'>
                  <span className='flex items-center pl-1 text-[12px] md:text-[14px]'>
                    {lang?.common?.select_your_city}
                  </span>
                  <span className='flex items-center'>
                    <MdLocationPin className='h-[20px] w-[20px] text-[#fff]' />
                    <span className=' block truncate'>{selected}</span>
                  </span>
                  <span className=' pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4'>
                    <AiFillCaretDown
                      className='h-5 w-5 text-white'
                      aria-hidden='true'
                    />
                  </span>
                </Listbox.Button>
              ) : (
                <Listbox.Button className='flex h-[34px] cursor-default items-center rounded-full border  border-primary-200 bg-white pl-[11px] pr-[13px]'>
                  <div className='flex items-center'>
                    <MdLocationOn className='h-[18px] w-[18px]' />
                    <span className='text-[14px] font-bold'>{selected}</span>
                  </div>
                  <MdArrowDropDown className='h-[20px] w-[20px]' />
                </Listbox.Button>
              )}

              <Transition
                show={open}
                as={Fragment}
                leave='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <Listbox.Options
                  className={cn(
                    'absolute right-0 z-10 mt-0   overflow-visible rounded-none bg-white   p-4 text-base shadow-lg ring-1 ring-black  ring-opacity-5 focus:outline-none sm:text-sm md:right-auto',
                    isFromLandingPage
                      ? 'custom-scrollbar h-[170px] w-[156px] overflow-y-scroll md:w-[286px]'
                      : 'w-[200px]'
                  )}
                >
                  <Listbox.Label className='text-[12px] text-low md:hidden'>
                    {lang?.common?.change_your_city}
                  </Listbox.Label>
                  {all_cities &&
                    all_cities.map((item) => (
                      <Listbox.Option
                        key={item.id}
                        className={({ active }) =>
                          cn(
                            active ? 'text-high ' : 'text-gray-900',
                            'relative w-full cursor-default select-none rounded-sm  p-2 hover:bg-gray-100'
                          )
                        }
                        value={item.slug}
                      >
                        {({ selected, active }) => (
                          <div className='flex items-center justify-between gap-3'>
                            <span
                              className={cn(
                                selected && 'font-bold',
                                'text-[16px]'
                              )}
                            >
                              {item.name}
                            </span>
                            <MdOutlineCheck
                              className={cn(
                                selected
                                  ? 'h-[16px] w-[16px] font-bold'
                                  : 'hidden'
                              )}
                            />
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    );
  else
    return (
      <Skeleton
        className={cn(
          'rounded-full',
          isFromLandingPage ? 'h-[56px] w-[186px] ' : 'h-[34px] w-[128px] '
        )}
      />
    );
};

export default React.memo(SelectCity);
